
// InputDataContext.js
import React, { createContext, useState, useEffect, useContext, ReactNode, FC } from 'react';

interface User {
  id: number;
  username: string;
  email: string;
}

// Define the context type
interface LoginContextType {
  user: User | null;
  setUser: (user: User | null) => void;
}

// Create the context with the appropriate type
const LoginContext = createContext<LoginContextType | undefined>(undefined);

// Define the provider's props
interface LoginProviderProps {
  children: ReactNode;
}


export const LoginProvider: FC<LoginProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Check if there's user data in localStorage
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null; // Initialize with saved data or null
  });

  // Whenever user changes, update localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  return (
    <LoginContext.Provider value={{ user, setUser }}>
      {children}
    </LoginContext.Provider>
  );
};

// Custom hook for consuming login context
export const useLoginData = (): LoginContextType => {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error('useLoginData must be used within a LoginProvider');
  }
  return context;
};
